import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { OnboardingPagePreset } from 'components/features/creditCard/creditCardOnboarding/OnboardingPagePreset/OnboardingPagePreset'
import styles from 'components/features/creditCard/creditCardOnboarding/ReviewStepPage/ReviewStepPage.styl'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { ReadOnlyField } from 'components/features/creditCard/creditCardOnboarding/ReadOnlyField/ReadOnlyField'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import Button from 'components/features/pfm/Button/Button'
import { Link } from 'react-router-dom'
import { CCOnboardingInitiateAction } from 'components/features/creditCard/creditCardStore/actions'
import { personDataSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { InPlacePropEditor } from 'components/features/creditCard/creditCardOnboarding/InPlacePropEditor/InPlacePropEditor'
import { DATE_MASK } from 'constants/forms'
import { CURRENCY_MASK, maskDate, maskIncome, transformDateAfter, transformDateBefore, transformIncomeAfter } from 'components/features/creditCard/creditCardOnboarding/helpers'
import Scrollable from 'components/atoms/Scrollable'
import { INDICATOR_CHECK_STEP } from 'components/features/pfm/StepIndicator/StepIndicator'
import { resolveErrorById, TYPE_REQUIRED } from 'constants/errors'
import { PageAnchorHandler } from 'handlers/PageAnchorHandler/PageAnchorHandler'
import * as yup from 'yup'
import { APPLICATION_TERMS_PDF_URL, ECA_DOCUMENT_PDF_URL, PRIVACY_NOTICE_PDF_URL } from 'components/features/creditCard/creditCardStore/constants'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { capitalizeFirstLetter } from 'helpers/formatHelpers'
import { addScript, removeScript } from 'helpers/utilsHelpers'
import { ENV } from 'constants/app'


const SOCURE_SANDBOX_SDK_KEY = 'a273ec39-412d-456b-891f-bf1dcccad5b6'
const SOCURE_PROD_SDK_KEY = 'ca23ece0-f294-4961-8c42-b00f8cc0679a'
declare const devicer: any
type DevicerResponse = {
  sessionId: string
  result: string
}


const NAME_SCHEMA = yup.object({
  value: yup.string().required(TYPE_REQUIRED).test(
    'valid-name',
    (params) => `'${ params.value }' contains invalid characters`,
    (value) => /^[a-zA-Z '\-]+$/.test(`${ value }`),
  ),
})

const INCOME_SCHEMA = yup.object({
  value: yup.string().required(TYPE_REQUIRED).test(
    'over-zero',
    () => `Income must be over $0`,
    (displayValue) => {
      if (!displayValue) {
        return false
      }
      const value = displayValue.replace(/\$/g, '')
      return parseInt(value, 10) > 0
    }
  ),
})

export const ReviewStepPage: FC = () => {
  const prevCCPersonData = useStoreSelector(personDataSelector)
  const dispatch = useStoreDispatch()

  const errorsMap = useStoreSelector((state) => state.app.creditCard.initiateRequest.errorsMap)
  const error = useMemo(() => {
    if (!errorsMap) {
      return null
    }

    const [errorKey] = Object.keys(errorsMap)
    if (!errorKey) {
      return null
    }

    const errors = errorsMap[errorKey]
    if (!errors || !errors[0]) {
      return null
    }

    return resolveErrorById(errors[0] as any)
  }, [errorsMap])
  useEffect(() => {
    if (!error) {
      return
    }

    dispatch(activateAnchorActionCreator('onboarding_review_error', { block: 'end', behavior: 'smooth' }))
  }, [dispatch, error])

  const handleAddressEdit = useCallback(() => {
    dispatch(pushActionCreator('/credit-card/onboarding/address?next-step=review'))
  }, [dispatch])

  const handleSSNEdit = useCallback(() => {
    dispatch(pushActionCreator('/credit-card/onboarding/ssn?next-step=review'))
  }, [dispatch])

  const handlePhoneEdit = useCallback(() => {
    dispatch(pushActionCreator('/credit-card/onboarding/phone?next-step=review'))
  }, [dispatch])

  const addressValues = useMemo(() => {
    const { address } = prevCCPersonData ?? {}
    if (!address) {
      return ['']
    }

    return [
      `${ address.number } ${ address.street }`,
      `${ address.refinement }`,
      `${ address.city.name }, ${ address.city.state.code } ${ address.zip5 }`,
    ].map((str) => str.trim()).filter(Boolean)
  }, [prevCCPersonData])

  const phoneNumberValues = useMemo(() => {
    const { phone_number } = prevCCPersonData ?? {}
    if (!phone_number) {
      return ['']
    }

    const phoneParts = phone_number.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
    if (!phoneParts) {
      return [phone_number]
    }

    const [, part1, part2, part3] = phoneParts
    return [`(${ part1 }) ${ part2 }-${ part3 }`]
  }, [prevCCPersonData])

  const [sessionId, setSessionId] = useState('')

  useEffect(() => {
    // Socure Device Verification - Required for Railsr Card Application
    const script = addScript({
      src: `https://js.dvnfo.com/devicer.min.js`,
      id: 'devicer',
      onLoad: () => {
        const deviceFPOptions = {
          publicKey: ENV === 'prod' ? SOCURE_PROD_SDK_KEY : SOCURE_SANDBOX_SDK_KEY,
          endpoint: 'https://dvnfo.com/',
          userConsent: true,
          context: 'signup',
        }
        devicer.run(deviceFPOptions, function (response: DevicerResponse) {
          setSessionId(response.sessionId)
        })
      },
    })
    return () => removeScript({ id: script.id })
  }, [])

  const handleNextButton = useCallback(() => {
    dispatch(new CCOnboardingInitiateAction(sessionId))
  }, [dispatch, sessionId])

  const isLoading = useStoreSelector((state) => state.app.creditCard.initiateRequest.isLoading)

  const formChild = useMemo(() => {
    return (
      <>
        <div className={ styles.topNote } data-qa-marker='form-header'>
          The following information will be used to verify your identity and prequalify your credit card application.
        </div>
        <div className={ styles.fields }>
          <InPlacePropEditor
            className={ styles.field }
            label={ (
              <>
                Your First Name
                <sup>1</sup>
              </>
            ) }
            name='first_name'
            qaMarker='first-name'
            validationSchema={ NAME_SCHEMA }
          />
          <InPlacePropEditor
            className={ styles.field }
            label={ (
              <>
                Your Last Name
                <sup>1</sup>
              </>
            ) }
            name='last_name'
            qaMarker='last-name'
            validationSchema={ NAME_SCHEMA }
          />
          <InPlacePropEditor
            className={ styles.field }
            label='Date of Birth'
            qaMarker='date-of-birth'
            name='date_of_birth'
            mask={ DATE_MASK }
            maskValue={ maskDate }
            transformBefore={ transformDateBefore }
            transformAfter={ transformDateAfter }
          />
          <InPlacePropEditor
            className={ styles.field }
            label='Total Annual Income'
            qaMarker='annual-income'
            name='income'
            mask={ CURRENCY_MASK }
            maskValue={ maskIncome }
            transformAfter={ transformIncomeAfter }
            maxLength={ 11 }
            validationSchema={ INCOME_SCHEMA }
          />
          <ReadOnlyField
            className={ styles.field }
            label='Residential Address (No PO box)'
            values={ addressValues }
            onEdit={ handleAddressEdit }
            qaMarker='residential-address'
          />
          <ReadOnlyField
            className={ styles.field }
            label={ (
              <>
                Mobile Number
                <sup>2</sup>
              </>
            ) }
            values={ phoneNumberValues }
            onEdit={ handlePhoneEdit }
            qaMarker='phone-number'
          />
          <ReadOnlyField
            className={ styles.field }
            label='Last 4 SSN & ITIN'
            values={ !prevCCPersonData ? [''] : [`*** ** ${ prevCCPersonData?.ssn4 }`] }
            onEdit={ handleSSNEdit }
            qaMarker='ssn-last-4'
          />

          <InPlacePropEditor
            className={ styles.field }
            label='Contact Email Address'
            qaMarker='email-address'
            name='email'
          />
        </div>

        <br />

        <div data-anchor='privacy'>
          <div className={ styles.articlePlate }>
            <p className={ styles.subNote } data-qa-type='note'>
              Please read the terms before applying.
            </p>
            <Scrollable allowOuterScroll alwaysShowScrollbar contentClassName={ styles.article } data-qa-type='text'>
              <li><a href={ APPLICATION_TERMS_PDF_URL } target='_blank' rel='noopener noreferrer'>Application Terms &amp; Conditions</a></li>
              <li><a href={ PRIVACY_NOTICE_PDF_URL } target='_blank' rel='noopener noreferrer'>Privacy Notices</a></li>
              <li><a href={ ECA_DOCUMENT_PDF_URL } target='_blank' rel='noopener noreferrer'>Electronic Communications Agreement</a></li>
            </Scrollable>
          </div>
        </div>
        <br />
        <p className={ styles.subNote } data-qa-type='note'>
          By submitting, you agree to the Terms &amp conditions above, as well as apply.
        </p>
        <br />
        { !!error && (
          <PageAnchorHandler placement='onboarding_review_error'>
            <FloatingPaneNotification text={ capitalizeFirstLetter(error) } style='insight' />
            <br />
          </PageAnchorHandler>
        )}
        <Button
          text='Submit Application'
          onClick={ handleNextButton }
          fullWidth
          qaMarker='submit'
          isSubmitting={ isLoading }
        />
        <Link to='/credit-card'>
          <Button
            text='Finish later'
            style='textOnly'
            fullWidth
            qaMarker='finish-later'
          />
        </Link>

        <div className={ styles.bottomNote } data-qa-marker='footnotes'>
          The above information will be used to verify your identity and preapprove your credit card application.
          <br /><br />
          By agreeing, I acknowledge that I am providing written instructions under the Fair Credit Reporting Act authorizing
          Status Money Inc. and its affiliated partners to obtain my credit report from one or more of the credit reporting
          agencies. To check the rates you may be preapproved for, we will perform a soft credit inquiry that will not impact your
          credit score. In addition, I acknowledge and agree that I wish to open a Credit Card account in my name, and have the
          Credit Card(s) renewed and replaced periodically. I certify that the information I provided in this Application is
          accurate and complete to the best of my knowledge.
          <br /><br />
          <sup>1</sup>Your legal name is required for your application to be processed and will fail if any nickname is used.
          <br /><br />
          <sup>2</sup>Your mobile number will be used to communicate important messages. Standard carrier rates may be charged. See terms and conditions
        </div>
      </>
    )
  }, [addressValues, handleAddressEdit, prevCCPersonData, handleSSNEdit, phoneNumberValues, handlePhoneEdit, error, handleNextButton, isLoading])
  return (
    <OnboardingPagePreset
      step={ INDICATOR_CHECK_STEP }
      title='Review your personal information'
      card={ formChild }
      className={ styles.pageLayout }
      progressPercent={ 65 }
    />
  )
}
